import { ADMIN_PERMISSIONS, PERMISSIONS, ROLES, type Permission, type RoleSlug } from './permissions.js';

export interface SiteAdminLink {
  label: string;
  to: string;
  permission?: Permission;
  /** Si está definido, basta con uno de estos permisos. */
  anyPermissions?: Permission[];
}

export interface SiteAdminSection {
  label: string | null;
  links: SiteAdminLink[];
}

export interface SiteAdminTab {
  label: string;
  heading: string;
  sections: SiteAdminSection[];
}

export const SITE_ADMIN_TABS: Record<string, Omit<SiteAdminTab, 'heading'> & { heading?: string }> = {
  general: {
    label: 'General',
    heading: 'Administración del sitio',
    sections: [
      {
        label: 'Perfil',
        links: [{ label: 'Mi perfil', to: '/profile', permission: PERMISSIONS.PERFIL_VER }],
      },
      {
        label: 'Aprendizaje',
        links: [
          { label: 'Mis cursos', to: '/mis-cursos', permission: PERMISSIONS.MIS_CURSOS_VER },
          { label: 'Mi expediente', to: '/expediente', permission: PERMISSIONS.EXPEDIENTE_VER },
          { label: 'Mis certificados', to: '/mis-certificados', permission: PERMISSIONS.MIS_CREDENCIALES_VER },
          { label: 'Mis contratos', to: '/mis-contratos', permission: PERMISSIONS.MIS_CONTRATOS_VER },
        ],
      },
      {
        label: 'Credenciales',
        links: [
          { label: 'Gestionar credenciales', to: '/credenciales', permission: PERMISSIONS.CREDENCIALES_VER },
          { label: 'Emitir una credencial', to: '/credenciales/emitir', permission: PERMISSIONS.CREDENCIALES_EMITIR },
          { label: 'Plantillas de credenciales', to: '/credenciales/plantillas', permission: PERMISSIONS.CREDENCIALES_PLANTILLAS },
        ],
      },
    ],
  },
  users: {
    label: 'Usuarios',
    heading: 'Usuarios',
    sections: [
      {
        label: 'Cuentas',
        links: [
          { label: 'Explorar lista de usuarios', to: '/usuarios', permission: PERMISSIONS.USUARIOS_VER },
          { label: 'Agregar un nuevo usuario', to: '/usuarios/create', permission: PERMISSIONS.USUARIOS_CREAR },
          { label: 'Importar usuarios JSON', to: '/usuarios/importar', permission: PERMISSIONS.USUARIOS_CREAR },
          { label: 'Grupos', to: '/grupos', permission: PERMISSIONS.GRUPOS_VER },
          { label: 'Matrículas', to: '/inscripciones', permission: PERMISSIONS.INSCRIPCIONES_VER },
          { label: 'Matricular estudiantes', to: '/inscripciones/bulk', permission: PERMISSIONS.INSCRIPCIONES_ASIGNAR },
          { label: 'Campos del perfil de usuario', to: '/campos-personalizados', permission: PERMISSIONS.CAMPOS_USUARIO_VER },
        ],
      },
      {
        label: 'Permisos',
        links: [
          { label: 'Definir roles', to: '/roles', permission: PERMISSIONS.ROLES_VER },
          { label: 'Gestionar permisos', to: '/permisos', permission: PERMISSIONS.PERMISOS_VER },
        ],
      },
    ],
  },
  courses: {
    label: 'Cursos',
    heading: 'Cursos',
    sections: [
      {
        label: 'Cursos',
        links: [
          { label: 'Gestionar categorías de cursos', to: '/categorias', permission: PERMISSIONS.CATEGORIAS_VER },
          { label: 'Gestionar cursos', to: '/courses', permission: PERMISSIONS.CURSOS_VER },
          { label: 'Agregar un nuevo curso', to: '/courses/create', permission: PERMISSIONS.CURSOS_CREAR },
        ],
      },
      {
        label: 'Contenido',
        links: [
          { label: 'Biblioteca de objetos de aprendizaje', to: '/library', permission: PERMISSIONS.BIBLIOTECA_VER },
          { label: 'Gestionar evaluaciones', to: '/evaluaciones', permission: PERMISSIONS.EVALUACIONES_VER },
          { label: 'Banco de preguntas', to: '/preguntas', permission: PERMISSIONS.PREGUNTAS_VER },
          { label: 'Gestionar archivos multimedia', to: '/media', permission: PERMISSIONS.MULTIMEDIA_VER },
        ],
      },
    ],
  },
  grades: {
    label: 'Calificaciones',
    heading: 'Calificaciones',
    sections: [
      {
        label: 'Calificaciones',
        links: [
          { label: 'Libro de calificaciones', to: '/calificaciones', permission: PERMISSIONS.CALIFICACIONES_VER },
        ],
      },
      {
        label: 'Seguimiento',
        links: [
          { label: 'Progreso de estudiantes', to: '/progreso', permission: PERMISSIONS.PROGRESO_VER },
          {
            label: 'Expedientes académicos',
            to: '/expediente/admin',
            anyPermissions: [PERMISSIONS.EXPEDIENTE_VER_TODOS, PERMISSIONS.EXPEDIENTE_ADMINISTRAR],
          },
        ],
      },
    ],
  },
  plugins: {
    label: 'Plugins',
    heading: 'Plugins',
    sections: [
      {
        label: 'Examen práctico',
        links: [
          { label: 'Fichas de evaluación', to: '/examen-practico', permission: PERMISSIONS.EVALUACIONES_VER },
          { label: 'Nueva ficha', to: '/examen-practico/nuevo', permission: PERMISSIONS.EVALUACIONES_CREAR },
        ],
      },
      {
        label: 'Vehículos',
        links: [
          { label: 'Lista de vehículos', to: '/examen-practico/vehiculos', permission: PERMISSIONS.EVALUACIONES_VER },
          { label: 'Agregar un vehículo', to: '/examen-practico/vehiculos/nuevo', permission: PERMISSIONS.EVALUACIONES_CREAR },
        ],
      },
      {
        label: 'H5P',
        links: [
          { label: 'Biblioteca H5P', to: '/h5p', permission: PERMISSIONS.H5P_VER },
          { label: 'Librerías H5P', to: '/h5p/libraries', permission: PERMISSIONS.H5P_DEPENDENCIAS_VER },
        ],
      },
      {
        label: 'Contratos',
        links: [
          { label: 'Plantillas de contrato', to: '/contratos/plantillas', permission: PERMISSIONS.CONTRATOS_PLANTILLAS },
          { label: 'Contratos generados', to: '/contratos', permission: PERMISSIONS.CONTRATOS_VER },
        ],
      },
      {
        label: 'Políticas legales',
        links: [
          { label: 'Plantillas de políticas', to: '/politicas-legales', permission: PERMISSIONS.POLITICAS_LEGALES_VER },
          { label: 'Aceptaciones registradas', to: '/politicas-legales/aceptaciones', permission: PERMISSIONS.POLITICAS_LEGALES_VER },
        ],
      },
      {
        label: 'Soporte',
        links: [
          { label: 'Mesa de ayuda', to: '/soporte/admin', permission: PERMISSIONS.SOPORTE_VER },
        ],
      },
      {
        label: 'Tiempo de sesión',
        links: [
          { label: 'Configuración de sesión', to: '/progreso/configuracion-sesion', permission: PERMISSIONS.PROGRESO_ADMINISTRAR },
          {
            label: 'Tiempo por alumno',
            to: '/progreso/tiempo-conexion',
            anyPermissions: [PERMISSIONS.PROGRESO_TIEMPO_VER, PERMISSIONS.PROGRESO_ADMINISTRAR],
          },
        ],
      },
    ],
  },
  design: {
    label: 'Diseño y assets',
    heading: 'Diseño y assets',
    sections: [
      {
        label: 'Tema',
        links: [
          { label: 'Assets del sitio', to: '/apariencia', permission: PERMISSIONS.APARIENCIA_VER },
        ],
      },
    ],
  },
  development: {
    label: 'Desarrollo',
    heading: 'Desarrollo',
    sections: [
      {
        label: 'Estado del servidor',
        links: [
          { label: 'Estado del servidor', to: '/desarrollo/estado-del-servidor', permission: PERMISSIONS.DESARROLLO_VER },
        ],
      },
    ],
  },
};

const SITE_ADMIN_TABS_FOR_ADMIN_ROLE = new Set(['design', 'development']);

export interface FilterSiteAdminTabsOptions {
  hasRole?: (role: RoleSlug) => boolean;
}

function hasPermissionBeyondAdminScope(hasPermission: (permission: Permission) => boolean): boolean {
  return Object.values(PERMISSIONS).some(
    (permission) => !ADMIN_PERMISSIONS.includes(permission) && hasPermission(permission),
  );
}

/** Rol admin «base»: solo apariencia y desarrollo, sin permisos ampliados vía otros roles o asignación directa. */
export function isRestrictedAdminNavigation(
  hasPermission: (permission: Permission) => boolean,
  options?: FilterSiteAdminTabsOptions,
): boolean {
  if (options?.hasRole?.(ROLES.SUPER_ADMIN) === true || options?.hasRole?.(ROLES.DEVELOPER) === true) return false;
  if (options?.hasRole?.(ROLES.ADMIN) !== true) return false;
  if (hasPermissionBeyondAdminScope(hasPermission)) return false;
  return true;
}

export function filterVisibleSiteAdminTabs(
  hasPermission: (permission: Permission) => boolean,
  options?: FilterSiteAdminTabsOptions,
): Record<string, SiteAdminTab> {
  const visible: Record<string, SiteAdminTab> = {};
  const isAdminOnly = isRestrictedAdminNavigation(hasPermission, options);

  for (const [key, tab] of Object.entries(SITE_ADMIN_TABS)) {
    if (isAdminOnly && !SITE_ADMIN_TABS_FOR_ADMIN_ROLE.has(key)) continue;
    const sections = tab.sections
      .map((section) => ({
        ...section,
        links: section.links.filter((link) => {
          if (link.anyPermissions?.length) return link.anyPermissions.some((permission) => hasPermission(permission));
          return !link.permission || hasPermission(link.permission);
        }),
      }))
      .filter((section) => section.links.length > 0);

    if (sections.length > 0) {
      visible[key] = {
        label: tab.label,
        heading: tab.heading ?? tab.label,
        sections,
      };
    }
  }

  return visible;
}

/** Rutas legacy `/admin/*` del API hub → rutas React reales. */
export const ADMIN_LEGACY_ROUTE_MAP: [string, string][] = [
  ['/admin/users', '/usuarios'],
  ['/admin/categories', '/categorias'],
  ['/admin/courses', '/courses'],
  ['/admin/library', '/library'],
  ['/admin/media', '/media'],
  ['/admin/h5p', '/h5p'],
  ['/admin/questions', '/preguntas'],
  ['/admin/assessments', '/evaluaciones'],
  ['/admin/enrollments', '/inscripciones'],
  ['/admin/groups', '/grupos'],
  ['/admin/progress', '/progreso'],
  ['/admin/grades', '/calificaciones'],
  ['/admin/academic-records', '/expediente/admin'],
  ['/admin/credentials', '/credenciales'],
  ['/admin/contracts', '/contratos'],
  ['/admin/legal-policies', '/politicas-legales'],
  ['/admin', '/administracion'],
];

export function resolveAdminLegacyPath(pathname: string): string | null {
  const normalized = pathname.replace(/\/$/, '') || '/';
  for (const [from, to] of ADMIN_LEGACY_ROUTE_MAP) {
    if (normalized === from) return to;
    if (normalized.startsWith(`${from}/`)) {
      return `${to}${normalized.slice(from.length)}`;
    }
  }
  return null;
}
